/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useErrorState } from '../../shared/useErrorState';
import { VStack, Image } from 'chakra-v3';
import type { UserImageComponent } from '@karya/core';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedComponent } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import { ChatBubble } from '../../shared/ChatBubble';
import { Button } from '../../../../../components/buttons/Index';
import { InvalidFileTypeError, FileSizeExceededError, InvalidImageError } from './ErrorMessages';
import { useNext } from '../../../hooks/useNext';

export const UserImage: React.FC<ComponentRendererProps> = ({ node, isCurrent, controller }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useErrorState();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUrlRef = useRef<string | null>(null);

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    // Capture current values at effect time
    const currentPreviewUrl = previewUrl;
    const currentPendingUrl = pendingUrlRef.current;

    return () => {
      // Revoke URL from state
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
      // Revoke pending URL (created but not yet set in state)
      if (currentPendingUrl) {
        URL.revokeObjectURL(currentPendingUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setError(null);

    // Revoke old preview URL if it exists
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(() => new InvalidFileTypeError('image'));
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    // Validate file size (reasonable default: 20MB for images)
    const maxFileSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxFileSize) {
      const fileSizeMB = file.size / (1024 * 1024);
      setError(() => new FileSizeExceededError(fileSizeMB, 20));
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    // Validate image dimensions (optional - check if image loads correctly)
    const url = URL.createObjectURL(file);
    pendingUrlRef.current = url; // Track pending URL for cleanup

    const img = new window.Image();
    img.onload = () => {
      // Image loaded successfully, dimensions are valid
      pendingUrlRef.current = null; // Clear pending ref
      setPreviewUrl(url);
      setSelectedFile(file);
    };
    img.onerror = () => {
      setError(() => new InvalidImageError());
      URL.revokeObjectURL(url);
      pendingUrlRef.current = null; // Clear pending ref
      setSelectedFile(null);
      setPreviewUrl(null);
    };
    img.src = url;
  };

  const teardownFn = useCallback(
    (next: () => void) => {
      if (!previewUrl || !selectedFile) {
        return;
      }

      // Final validation before submission
      if (!selectedFile.type.startsWith('image/')) {
        setError(() => new InvalidFileTypeError('image'));
        return;
      }
      const maxFileSize = 20 * 1024 * 1024; // 20MB
      if (selectedFile.size > maxFileSize) {
        const fileSizeMB = selectedFile.size / (1024 * 1024);
        setError(() => new FileSizeExceededError(fileSizeMB, 20));
        return;
      }

      // Write output - image is always an output param for UserImageComponent
      const component = getTypedComponent<UserImageComponent>(node);
      controller.writeOutput(component.image, previewUrl);

      // Only call next() when validation passes and output is written
      next();
    },
    [previewUrl, selectedFile, node, controller, setError],
  );

  useNext({
    controller,
    teardownFn,
    strategy: 'MANUAL',
    isCurrent,
  });

  return (
    <ChatBubble
      containerProps={{ w: 'fit-content', minW: { base: '200px', md: '300px' }, maxW: '70vw', alignSelf: 'flex-end' }}
    >
      <VStack gap="16px" align="stretch">
        <Text variant="body">Upload an image</Text>
        {error && (
          <Text variant="body" color="error.solid">
            {error.message}
          </Text>
        )}
        {!previewUrl && (
          <VStack gap="12px">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={!isCurrent}
              style={{ display: 'none' }}
            />
            <Button onClick={() => fileInputRef.current?.click()} variant="secondary" disabled={!isCurrent} w="100%">
              Choose Image
            </Button>
          </VStack>
        )}
        {previewUrl && (
          <VStack gap="12px">
            <Image src={previewUrl} alt="Preview" maxH="400px" w="auto" />
          </VStack>
        )}
      </VStack>
    </ChatBubble>
  );
};
